import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type StageKey = "intake" | "analysis" | "coordination" | "decision";

export const PLAYBACK_PHASE_MIN_MS = 300;
export const PLAYBACK_PHASE_MAX_MS = 10000;
export const PLAYBACK_SCALE = 1;
export const PLAYBACK_SYNTHESISER_MS = 1500;
export const PLAYBACK_CARD_STAGGER_MS = 300;

// Delay between a stage's playback finishing and the next stage being
// triggered when auto-run is active. Gives the viewer time to read the
// outcome card before the journey jumps forward.
export const AUTO_RUN_STAGE_PAUSE_MS = 1500;

const KEY_PREFIX = "playback:";
const AUTO_RUN_KEY_PREFIX = "autorun:";

function key(appId: string, stage: StageKey): string {
  return `${KEY_PREFIX}${appId}:${stage}`;
}

export function markStageForPlayback(appId: string, stage: StageKey): void {
  try {
    sessionStorage.setItem(key(appId, stage), "1");
  } catch {
    // sessionStorage unavailable — playback just won't fire, not worth surfacing.
  }
}

export function consumePlayback(appId: string, stage: StageKey): boolean {
  try {
    const k = key(appId, stage);
    const v = sessionStorage.getItem(k);
    if (v === "1") {
      sessionStorage.removeItem(k);
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

export function clearAllPlaybackFlags(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(KEY_PREFIX)) toRemove.push(k);
    }
    for (const k of toRemove) sessionStorage.removeItem(k);
  } catch {
    // ignore
  }
}

export function scalePhaseMs(durationMs: number): number {
  const scaled = durationMs / PLAYBACK_SCALE;
  return Math.max(
    PLAYBACK_PHASE_MIN_MS,
    Math.min(PLAYBACK_PHASE_MAX_MS, scaled),
  );
}

type PlaybackStageContextValue = {
  playingStage: StageKey | null;
  setPlayingStage: (s: StageKey | null) => void;
};

const PlaybackStageContext = createContext<PlaybackStageContextValue>({
  playingStage: null,
  setPlayingStage: () => {},
});

export function PlaybackStageProvider({ children }: { children: ReactNode }) {
  const [playingStage, setPlayingStage] = useState<StageKey | null>(null);
  const value = useMemo(
    () => ({ playingStage, setPlayingStage }),
    [playingStage],
  );
  return (
    <PlaybackStageContext.Provider value={value}>
      {children}
    </PlaybackStageContext.Provider>
  );
}

export function usePlaybackStage(): PlaybackStageContextValue {
  return useContext(PlaybackStageContext);
}

// -----------------------------------------------------------------------------
// Auto-run: "play the whole journey" execution mode
// -----------------------------------------------------------------------------

function autoRunKey(appId: string): string {
  return `${AUTO_RUN_KEY_PREFIX}${appId}`;
}

function readAutoRun(appId: string): boolean {
  try {
    return sessionStorage.getItem(autoRunKey(appId)) === "1";
  } catch {
    return false;
  }
}

function writeAutoRun(appId: string, on: boolean): void {
  try {
    if (on) sessionStorage.setItem(autoRunKey(appId), "1");
    else sessionStorage.removeItem(autoRunKey(appId));
  } catch {
    // ignore — feature degrades to manual mode
  }
}

type AutoRunContextValue = {
  autoRunAppId: string | null;
  startAutoRun: (appId: string) => void;
  stopAutoRun: () => void;
  isAutoRunning: (appId: string) => boolean;
};

const AutoRunContext = createContext<AutoRunContextValue>({
  autoRunAppId: null,
  startAutoRun: () => {},
  stopAutoRun: () => {},
  isAutoRunning: () => false,
});

export function AutoRunProvider({
  appId,
  children,
}: {
  appId: string;
  children: ReactNode;
}) {
  const [autoRunAppId, setAutoRunAppId] = useState<string | null>(() =>
    readAutoRun(appId) ? appId : null,
  );

  // Re-read when the active application changes (navigation between apps).
  useEffect(() => {
    setAutoRunAppId(readAutoRun(appId) ? appId : null);
  }, [appId]);

  const startAutoRun = useCallback((id: string) => {
    writeAutoRun(id, true);
    setAutoRunAppId(id);
  }, []);

  const stopAutoRun = useCallback(() => {
    if (autoRunAppId) writeAutoRun(autoRunAppId, false);
    setAutoRunAppId(null);
  }, [autoRunAppId]);

  const isAutoRunning = useCallback(
    (id: string) => autoRunAppId === id,
    [autoRunAppId],
  );

  const value = useMemo(
    () => ({ autoRunAppId, startAutoRun, stopAutoRun, isAutoRunning }),
    [autoRunAppId, startAutoRun, stopAutoRun, isAutoRunning],
  );

  return (
    <AutoRunContext.Provider value={value}>{children}</AutoRunContext.Provider>
  );
}

export function useAutoRun(): AutoRunContextValue {
  return useContext(AutoRunContext);
}
